import { useEffect, useRef, useState } from 'react';
import { computeCoverCrop } from '../lib/downscale';
import { decideEyesOpen, type EyesOpenResult } from '../lib/eyeOpenness';
import { createVideoFaceDetector, type VideoFaceDetector } from '../lib/faceDetector';
import { evaluateFraming, type FrameSize } from '../lib/framing';
import { createManualFallbackTracker } from '../lib/manualFallback';
import { createStatusDebouncer } from '../lib/statusDebounce';
import type { DetectedFace, GuidanceStatus } from '../types';

const DETECT_INTERVAL_MS = 200;
/** Consecutive 'ready' ticks required before the shutter unlocks (~600ms stable). */
const STABLE_READY_TICKS = 3;
/** Ticks a new status must hold before the UI switches (anti-flicker). */
const STATUS_DEBOUNCE_TICKS = 2;
/**
 * Ticks a well-framed face may stay blocked by the eye gate before the manual
 * shutter unlocks anyway (~5s at 200ms). Escape hatch so an eye-gate misfire
 * can never hard-stick a legitimate user — the server still validates the shot.
 */
const MANUAL_FALLBACK_TICKS = 25;

export interface FaceGuidanceState {
  guidance: GuidanceStatus;
  /** True when the shutter should be enabled. */
  shutterReady: boolean;
  /** Latest detected face boxes (source-frame pixels) for the live overlay. */
  faces: DetectedFace[];
  /** Source frame size the boxes are relative to; null until detection ticks. */
  frameSize: FrameSize | null;
  /** Eye-openness signals (dev tuning readout); null when not evaluated. */
  eyeOpenness: EyesOpenResult | null;
  /**
   * True once a well-framed face has been stuck on the eye gate long enough
   * that the manual shutter was unlocked as a fallback — the UI shows a hint.
   */
  manualFallback: boolean;
}

/**
 * Runs the face detector against the live preview and reduces the result to
 * a single guidance status. The shutter unlocks only after the framing has
 * been 'ready' for a few consecutive ticks, so a face sweeping past the
 * camera can't trigger an instant capture.
 *
 * If the detector cannot initialize (wasm/model unavailable), the hook
 * degrades to 'detector-unavailable' and unlocks the shutter — the user
 * still gets framing instructions, and the review step remains the human
 * gate before anything is sent to ESS.
 */
export function useFaceGuidance(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  active: boolean,
): FaceGuidanceState {
  const [guidance, setGuidance] = useState<GuidanceStatus>('detecting');
  const [shutterReady, setShutterReady] = useState(false);
  const [faces, setFaces] = useState<DetectedFace[]>([]);
  const [frameSize, setFrameSize] = useState<FrameSize | null>(null);
  const [eyeOpenness, setEyeOpenness] = useState<EyesOpenResult | null>(null);
  const [manualFallback, setManualFallback] = useState(false);
  const readyTicksRef = useRef(0);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    let detector: VideoFaceDetector | null = null;
    let timer: ReturnType<typeof setInterval> | null = null;
    let eyesWereOpen = false;
    const debounce = createStatusDebouncer<GuidanceStatus>(
      STATUS_DEBOUNCE_TICKS,
      'detecting',
    );
    const fallback = createManualFallbackTracker(MANUAL_FALLBACK_TICKS);

    async function start() {
      try {
        detector = await createVideoFaceDetector();
      } catch {
        if (!cancelled) {
          setGuidance('detector-unavailable');
          setShutterReady(true);
        }
        return;
      }
      if (cancelled) {
        detector.close();
        return;
      }
      timer = setInterval(() => {
        const video = videoRef.current;
        if (!video || video.videoWidth === 0 || !detector) return;
        const frame = { width: video.videoWidth, height: video.videoHeight };
        // WYSIWYG: guidance and capture both operate on the region the
        // object-cover preview shows. Faces in the camera frame but outside
        // the visible crop (e.g. someone at the wide frame's edge) neither
        // trigger 'multiple-faces' nor end up in the captured photo.
        const crop = computeCoverCrop(frame, {
          width: video.clientWidth,
          height: video.clientHeight,
        });
        const detected = detector
          .detect(video, performance.now())
          .filter((f) => {
            const cx = f.x + f.width / 2;
            const cy = f.y + f.height / 2;
            return (
              cx >= crop.x &&
              cx <= crop.x + crop.width &&
              cy >= crop.y &&
              cy <= crop.y + crop.height
            );
          });
        let status = evaluateFraming(detected, frame);

        // Eye-openness gate: eyes must be open (EAR geometry AND eyeBlink
        // agree) before the shutter arms, so auto-capture can't fire mid-blink.
        // Runs only when framing is otherwise ready. `eyesWereOpen` feeds the
        // hysteresis so a borderline value can't flicker the verdict.
        // (Object occlusion is judged server-side by Rekognition, not here.)
        let eyeState: EyesOpenResult | null = null;
        const framingReady = status === 'ready';
        if (framingReady && detected[0]?.eyeOpenness) {
          eyeState = decideEyesOpen(detected[0].eyeOpenness, eyesWereOpen);
          eyesWereOpen = eyeState.eyesOpen;
          if (!eyeState.eyesOpen) status = 'face-unclear';
        }

        // Manual-shutter fallback: if the face is well-framed but the eye gate
        // keeps it blocked for ~5s, unlock the shutter anyway so an eye-gate
        // misfire can't hard-stick a real user (auto-capture still needs a
        // genuine 'ready'; the server validates the forced shot).
        const blockedOnEyesOnly = framingReady && eyeState !== null && !eyeState.eyesOpen;
        const manualUnlocked = fallback.update(blockedOnEyesOnly);

        // Debounce: a new status must hold for 2 ticks before the UI follows,
        // but the ready-streak counter uses the raw status so the shutter
        // timing is unchanged.
        readyTicksRef.current = status === 'ready' ? readyTicksRef.current + 1 : 0;
        setFaces(detected);
        setFrameSize(frame);
        setEyeOpenness(eyeState);
        setGuidance(debounce(status));
        setShutterReady(readyTicksRef.current >= STABLE_READY_TICKS || manualUnlocked);
        setManualFallback(manualUnlocked);
      }, DETECT_INTERVAL_MS);
    }

    void start();
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      detector?.close();
      readyTicksRef.current = 0;
      fallback.reset();
      setGuidance('detecting');
      setShutterReady(false);
      setFaces([]);
      setFrameSize(null);
      setEyeOpenness(null);
      setManualFallback(false);
    };
  }, [videoRef, active]);

  return { guidance, shutterReady, faces, frameSize, eyeOpenness, manualFallback };
}

// The detection loop lives in a long-lived interval whose closure captures the
// detector and the lib functions (framing, eyeOpenness, …). A Vite hot update
// re-renders the page but does NOT re-run the effect, so the OLD loop keeps
// judging frames with stale code — observed as "threshold change didn't take
// effect until pnpm dev restart". Bail out of HMR for this module graph and
// force a full reload instead.
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    import.meta.hot?.invalidate();
  });
}
